import React, { useState } from 'react';
import { Sparkles, Copy, Check, Crown, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'react-toastify';

interface ContentHooksGeneratorProps {
  clipId: string;
  clipTitle: string;
  isPro: boolean;
  onUpgrade: () => void;
}

interface HooksContent {
  hooks: string[];
  captions: string[];
  hashtags: string[];
  descriptions: {
    short: string;
    long: string;
  };
  generated_by: string;
}

const ContentHooksGenerator: React.FC<ContentHooksGeneratorProps> = ({
  clipId,
  clipTitle,
  isPro,
  onUpgrade
}) => {
  const [loading, setLoading] = useState(false);
  const [content, setContent] = useState<HooksContent | null>(null);
  const [copiedItems, setCopiedItems] = useState<Set<string>>(new Set());
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['hooks']));

  const generateContent = async () => {
    if (!isPro) {
      onUpgrade();
      return;
    }

    setLoading(true);
    try {
      const { clipApi } = await import('../services/api');
      const data = await clipApi.generateHooksAndCaptions(clipId);
      
      setContent(data.content);
      setExpandedSections(new Set(['hooks', 'captions', 'hashtags', 'descriptions']));
      toast.success('✨ Content generated successfully!');
    } catch (error: any) {
      console.error('Error generating content:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to generate content';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text: string, itemId: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedItems(prev => new Set(prev).add(itemId));
      toast.success('📋 Copied to clipboard!');
      
      // Reset copied state after 2 seconds
      setTimeout(() => {
        setCopiedItems(prev => {
          const newSet = new Set(prev);
          newSet.delete(itemId);
          return newSet;
        });
      }, 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
      toast.error('Failed to copy to clipboard');
    }
  };

  const toggleSection = (section: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(section)) {
        newSet.delete(section);
      } else {
        newSet.add(section);
      }
      return newSet;
    });
  };

  return (
    <div className="card">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <Sparkles className="h-6 w-6 text-red-600" />
            <h2 className="text-lg font-semibold text-gray-900">
              AI Hooks & Captions Generator
            </h2>
            {!isPro && (
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800">
                <Crown className="h-3 w-3 mr-1" />
                PRO
              </span>
            )}
          </div>
          <p className="text-sm text-gray-600">
            Generate attention-grabbing hooks and retention-focused captions for your clip
          </p>
        </div>

        <button
          onClick={generateContent}
          disabled={loading}
          className={`btn-primary flex items-center gap-2 whitespace-nowrap ${
            loading ? 'opacity-75 cursor-wait' : ''
          } ${!isPro ? 'bg-red-600 hover:bg-red-700' : ''}`}
        >
          {loading ? (
            <>
              <div className="loading-spinner mr-2"></div>
              Generating...
            </>
          ) : content ? (
            <>
              <RefreshCw className="h-4 w-4" />
              Regenerate
            </>
          ) : (
            <>
              <Sparkles className="h-4 w-4" />
              {isPro ? 'Generate Content' : 'Upgrade to Generate'}
            </>
          )}
        </button>
      </div>

      {/* Pro Feature Notice */}
      {!isPro && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start gap-3">
            <Crown className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-red-900">
                Upgrade to Pro to unlock AI-powered content generation
              </p>
              <p className="text-xs text-red-700 mt-1">
                Get hooks, captions, and hashtags optimized for maximum engagement and retention
              </p>
            </div>
            <button
              onClick={onUpgrade}
              className="btn-primary text-xs px-3 py-1.5 whitespace-nowrap"
            >
              Upgrade Now
            </button>
          </div>
        </div>
      )}

      {/* Content Display */}
      {content && (
        <div className="space-y-4">
          {/* Hooks Section */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <button
              onClick={() => toggleSection('hooks')}
              className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors flex items-center justify-between"
            >
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-gray-900">
                  🔥 Attention-Grabbing Hooks
                </span>
                <span className="text-xs text-gray-500">
                  ({content.hooks.length})
                </span>
              </div>
              {expandedSections.has('hooks') ? (
                <ChevronUp className="h-4 w-4 text-gray-500" />
              ) : (
                <ChevronDown className="h-4 w-4 text-gray-500" />
              )}
            </button>
            
            {expandedSections.has('hooks') && (
              <div className="p-4 space-y-3">
                {content.hooks.map((hook, index) => (
                  <div
                    key={`hook-${index}`}
                    className="group relative p-3 bg-white border border-gray-200 rounded-lg hover:border-red-300 hover:shadow-sm transition-all"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <p className="text-sm text-gray-900 flex-1">{hook}</p>
                      <button
                        onClick={() => copyToClipboard(hook, `hook-${index}`)}
                        className="flex-shrink-0 p-1.5 rounded hover:bg-gray-100 transition-colors"
                        title="Copy to clipboard"
                      >
                        {copiedItems.has(`hook-${index}`) ? (
                          <Check className="h-4 w-4 text-green-600" />
                        ) : (
                          <Copy className="h-4 w-4 text-gray-400 group-hover:text-gray-600" />
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Captions Section */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <button
              onClick={() => toggleSection('captions')}
              className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors flex items-center justify-between"
            >
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-gray-900">
                  ✍️ Engaging Captions
                </span>
                <span className="text-xs text-gray-500">
                  ({content.captions.length})
                </span>
              </div>
              {expandedSections.has('captions') ? (
                <ChevronUp className="h-4 w-4 text-gray-500" />
              ) : (
                <ChevronDown className="h-4 w-4 text-gray-500" />
              )}
            </button>
            
            {expandedSections.has('captions') && (
              <div className="p-4 space-y-3">
                {content.captions.map((caption, index) => (
                  <div
                    key={`caption-${index}`}
                    className="group relative p-3 bg-white border border-gray-200 rounded-lg hover:border-red-300 hover:shadow-sm transition-all"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <p className="text-sm text-gray-900 flex-1 whitespace-pre-wrap">{caption}</p>
                      <button
                        onClick={() => copyToClipboard(caption, `caption-${index}`)}
                        className="flex-shrink-0 p-1.5 rounded hover:bg-gray-100 transition-colors"
                        title="Copy to clipboard"
                      >
                        {copiedItems.has(`caption-${index}`) ? (
                          <Check className="h-4 w-4 text-green-600" />
                        ) : (
                          <Copy className="h-4 w-4 text-gray-400 group-hover:text-gray-600" />
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Hashtags Section */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <button
              onClick={() => toggleSection('hashtags')}
              className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors flex items-center justify-between"
            >
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-gray-900">
                  #️⃣ Trending Hashtags
                </span>
                <span className="text-xs text-gray-500">
                  ({content.hashtags.length})
                </span>
              </div>
              {expandedSections.has('hashtags') ? (
                <ChevronUp className="h-4 w-4 text-gray-500" />
              ) : (
                <ChevronDown className="h-4 w-4 text-gray-500" />
              )}
            </button>
            
            {expandedSections.has('hashtags') && (
              <div className="p-4">
                <div className="flex flex-wrap gap-2">
                  {content.hashtags.map((tag, index) => (
                    <button
                      key={`tag-${index}`}
                      onClick={() => copyToClipboard(`#${tag}`, `tag-${index}`)}
                      className="group relative px-3 py-1.5 bg-white border border-gray-200 rounded-full hover:border-red-300 hover:bg-red-50 transition-all text-sm text-gray-700 hover:text-red-700 font-medium"
                    >
                      #{tag}
                      {copiedItems.has(`tag-${index}`) && (
                        <Check className="inline h-3 w-3 ml-1 text-green-600" />
                      )}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => copyToClipboard(
                    content.hashtags.map(t => `#${t}`).join(' '),
                    'all-tags'
                  )}
                  className="mt-3 text-xs text-red-600 hover:text-red-700 font-medium flex items-center gap-1"
                >
                  {copiedItems.has('all-tags') ? (
                    <>
                      <Check className="h-3 w-3" />
                      All hashtags copied!
                    </>
                  ) : (
                    <>
                      <Copy className="h-3 w-3" />
                      Copy all hashtags
                    </>
                  )}
                </button>
              </div>
            )}
          </div>

          {/* Descriptions Section */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <button
              onClick={() => toggleSection('descriptions')}
              className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors flex items-center justify-between"
            >
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-gray-900">
                  📝 Description Variants
                </span>
              </div>
              {expandedSections.has('descriptions') ? (
                <ChevronUp className="h-4 w-4 text-gray-500" />
              ) : (
                <ChevronDown className="h-4 w-4 text-gray-500" />
              )}
            </button>
            
            {expandedSections.has('descriptions') && (
              <div className="p-4 space-y-3">
                <div className="group relative p-3 bg-white border border-gray-200 rounded-lg hover:border-red-300 hover:shadow-sm transition-all">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <span className="text-xs font-semibold text-gray-500 uppercase">Short (TikTok/Shorts)</span>
                    <button
                      onClick={() => copyToClipboard(content.descriptions.short, 'desc-short')}
                      className="flex-shrink-0 p-1.5 rounded hover:bg-gray-100 transition-colors"
                      title="Copy to clipboard"
                    >
                      {copiedItems.has('desc-short') ? (
                        <Check className="h-4 w-4 text-green-600" />
                      ) : (
                        <Copy className="h-4 w-4 text-gray-400 group-hover:text-gray-600" />
                      )}
                    </button>
                  </div>
                  <p className="text-sm text-gray-900 whitespace-pre-wrap">{content.descriptions.short}</p>
                </div>

                <div className="group relative p-3 bg-white border border-gray-200 rounded-lg hover:border-red-300 hover:shadow-sm transition-all">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <span className="text-xs font-semibold text-gray-500 uppercase">Long (YouTube/Instagram)</span>
                    <button
                      onClick={() => copyToClipboard(content.descriptions.long, 'desc-long')}
                      className="flex-shrink-0 p-1.5 rounded hover:bg-gray-100 transition-colors"
                      title="Copy to clipboard"
                    >
                      {copiedItems.has('desc-long') ? (
                        <Check className="h-4 w-4 text-green-600" />
                      ) : (
                        <Copy className="h-4 w-4 text-gray-400 group-hover:text-gray-600" />
                      )}
                    </button>
                  </div>
                  <p className="text-sm text-gray-900 whitespace-pre-wrap">{content.descriptions.long}</p>
                </div>
              </div>
            )}
          </div>

          {/* Footer Info */}
          <div className="pt-2 flex items-center justify-between text-xs text-gray-500">
            <p>
              Generated using {content.generated_by === 'openai' ? 'AI (GPT-4)' : 'proven templates'}
            </p>
            <p className="text-gray-400">
              Click any item to copy to clipboard
            </p>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!content && !loading && (
        <div className="text-center py-12 px-4">
          <Sparkles className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <p className="text-sm text-gray-600 mb-2">
            Generate AI-powered hooks and captions for "{clipTitle}"
          </p>
          <p className="text-xs text-gray-500">
            Get attention-grabbing content optimized for social media engagement
          </p>
        </div>
      )}
    </div>
  );
};

export default ContentHooksGenerator;

